import { useState, useEffect } from 'preact/hooks'
import { apiClient, authStore, InitData, navigerUd, returMaal } from '../core'
import { t, formatMoney } from '../core/i18n'
import { LoginWidget } from '../widgets/login/LoginWidget'
import { PaymentMethodPicker, hasPaymentChoice, type InlinePaymentMethod } from './PaymentMethodPicker'
import { CheckoutLegal, checkoutConsent, initialCheckoutLegalState, type CheckoutLegalState, earlyStartRelevantFor } from './CheckoutLegal'
import { firstPaymentTotalLabel } from '../core/first-payment'

export interface PurchasablePlan {
  id: string
  name: string
  price: number
  /** 'subscription' giver abonnementsnoten og afregningsperioden i ordreoversigten. */
  membershipCategory?: string | null
  validityDays?: number | null
  /** Planens startdato (ISO): starter den efter fortrydelsesfristen, spørges der ikke om tidlig start. */
  activatesFrom?: string | null
}

interface Props {
  plan: PurchasablePlan
  initData: InitData
  /** Handoff-URL til studiets egen prisside — fallback når inline-køb ikke kan gennemføres. */
  purchaseHref: string
  onClose: () => void
}

/** Rabatten serveren har regnet med samme regel som selve købet (GET .../price?discountCode=). */
interface AppliedDiscount {
  code: string
  amount: number
  finalPrice: number
  appliesTo: 'once' | 'first_payment' | 'every_payment'
  freeMonthsBeforeBilling: number | null
}

/**
 * «Træk den 1.» (pl-hs-fom-widget-beloeb): første træk regnet af serveren med samme funktion
 * og samme dag (studiets tidszone) som checkout. Sendes tilbage som expectedFirstChargeDkk.
 */
interface FirstCharge {
  amount: number
  folded: boolean
  recurringDkk: number
}

interface PriceResponse {
  price: number
  listPrice: number
  discount?: AppliedDiscount
  firstCharge?: FirstCharge
  /** Oprettelsesgebyr for denne kunde (pl-hs-fom-widget-gebyr). Mangler = intet gebyr. */
  signupFeeDkk?: number
  /** PT-ydelser (V538): adgang straks, intet trækkes i dag, første betaling på datoen (ISO). */
  deferredFirstCharge?: { date: string }
}

interface CheckoutResponse {
  success?: boolean
  requiresPayment?: boolean
  redirectUrl?: string
  reference?: string
  freeWithDiscount?: boolean
}

/**
 * PurchaseModal — inline klippekort-køb for embed-widgeten.
 *
 * Spejler BookingModal's flow, men for et medlemskabs-køb:
 *   - Logget ud: LoginWidget inline (skriver til authStore, subscriptionen nedenfor
 *     bytter branchen for købs-trinnet — kunden forlader aldrig studiets side).
 *   - Logget ind: «Køb»-knap → POST /api/widget/memberships/checkout → redirect til Stripe.
 *   - Rabatkode: «Har du en rabatkode?» → «Anvend» viser den rabatterede pris FØR
 *     betaling (serveren regner med samme regel som købet). En kode der ikke gælder
 *     planen giver serverens tydelige fejl ved feltet og falder ALDRIG stille bort.
 *   - Et 100 %-rabatteret køb (freeWithDiscount) redirecter hjem uden Stripe.
 *   - ENHVER ok:false (komplekst scenarie, Stripe ej konfigureret, plan fuld, ...) →
 *     fald tilbage til det nuværende handoff (samme mønster som BookingModal's
 *     PAYMENT_REQUIRED): kunden fortsætter på studiets egen prisside.
 */
const DUPLICATE_PAYMENT_CODE = 'api.memberships.checkout.duplicatePaymentInProgress'
/** Første træk er ændret siden visningen (ny dag i studiets tidszone): vis beløbet igen. */
const FIRST_CHARGE_CHANGED = 'FIRST_CHARGE_CHANGED'

export function PurchaseModal({ plan, initData, purchaseHref, onClose }: Props) {
  const [user, setUser] = useState(authStore.getUser())
  const [submitting, setSubmitting] = useState(false)
  const [needsHandoff, setNeedsHandoff] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // MobilePay eller kort, når studiet har begge (MobilePay forvalgt = serverens
  // standard). Uden vælgeren fik et både-studies kortkunde aldrig kort inline.
  const [payMethod, setPayMethod] = useState<InlinePaymentMethod>('mobilepay')
  const showPayPicker = hasPaymentChoice(initData)
  const s = t(initData.locale)
  // Ordreoversigt + samtykke før købet (jura-hs p44). Inline-køb er klippekort.
  const [legal, setLegal] = useState<CheckoutLegalState>(initialCheckoutLegalState)
  // Det beløb checkouten faktisk trækker den indloggede kunde (fx uden moms for en
  // under-30). Indtil det er hentet — og for en kunde der ikke er logget ind — vises
  // planens listepris. Før viste vinduet altid listeprisen, så en under-30 så "400 kr"
  // og blev trukket 320 kr.
  const [checkoutPrice, setCheckoutPrice] = useState<number | null>(null)
  const [discountOpen, setDiscountOpen] = useState(false)
  const [discountCode, setDiscountCode] = useState('')
  const [applied, setApplied] = useState<AppliedDiscount | null>(null)
  const [discountError, setDiscountError] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)
  const [firstCharge, setFirstCharge] = useState<FirstCharge | null>(null)
  // «Adgang nu, første betaling fra» (V538): intet trækkes i dag; datoen for første betaling.
  const [deferredDate, setDeferredDate] = useState<string | null>(null)
  // Oprettelsesgebyret checkout lægger oven i første betaling (egen linje, med i totalen).
  const [signupFee, setSignupFee] = useState(0)
  // Den rigtige første betaling fra checkout efter FIRST_CHARGE_CHANGED: vises og sendes igen.
  const [bekraeftet, setBekraeftet] = useState<number | null>(null)
  // Første træk fra den seneste rabat-opslag i samme klik (handleBuy → anvendRabat), før
  // state er opdateret.
  const senesteFoersteTraek: { vaerdi: FirstCharge | null; betaling: number | null } = { vaerdi: null, betaling: null }

  useEffect(() => authStore.subscribe(setUser), [])

  useEffect(() => {
    if (!user) {
      setCheckoutPrice(null)
      setFirstCharge(null)
      return
    }
    let aktiv = true
    void hentPris(null, () => aktiv)
    return () => { aktiv = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, plan.id])

  /** Pris og evt. første træk uden rabatkode (eller med den anvendte kode). */
  async function hentPris(code: string | null, aktiv: () => boolean = () => true) {
    const kodeParam = code ? `&discountCode=${encodeURIComponent(code)}` : ''
    const result = await apiClient.get<PriceResponse>(
      `/api/widget/memberships/price?pricingPlanId=${encodeURIComponent(plan.id)}${kodeParam}`,
      true,
    )
    if (!aktiv()) return result
    if (typeof result.data?.price === 'number') setCheckoutPrice(result.data.price)
    if (result.data) {
      setFirstCharge(result.data.firstCharge ?? null)
      setSignupFee(result.data.signupFeeDkk ?? 0)
      setDeferredDate(result.data.deferredFirstCharge?.date ?? null)
    }
    return result
  }

  /** Rabatkodens fejl. Serverens besked er allerede oversat til studiets sprog. */
  function visRabatFejl(code: string | undefined, error: string | undefined) {
    setApplied(null)
    setDiscountOpen(true)
    if (code === 'RATE_LIMITED') setDiscountError(s.discountTooManyAttempts)
    else setDiscountError(code === 'DISCOUNT_INVALID' && error ? error : s.discountInvalid)
  }

  /** Tjek koden og vis den rabatterede pris. Returnerer rabatten, eller null ved fejl. */
  async function anvendRabat(): Promise<AppliedDiscount | null> {
    const code = discountCode.trim().toUpperCase()
    if (!code) return null
    setApplying(true)
    setDiscountError(null)
    const result = await apiClient.get<PriceResponse>(
      `/api/widget/memberships/price?pricingPlanId=${encodeURIComponent(plan.id)}&discountCode=${encodeURIComponent(code)}`,
      true,
    )
    setApplying(false)
    if (result.error || !result.data?.discount) {
      visRabatFejl(result.code, result.error)
      return null
    }
    setCheckoutPrice(result.data.price)
    setFirstCharge(result.data.firstCharge ?? null)
    setSignupFee(result.data.signupFeeDkk ?? 0)
    setBekraeftet(null)
    senesteFoersteTraek.vaerdi = result.data.firstCharge ?? null
    senesteFoersteTraek.betaling = (result.data.firstCharge?.amount ?? result.data.discount.finalPrice) + (result.data.signupFeeDkk ?? 0)
    setApplied(result.data.discount)
    return result.data.discount
  }

  function fjernRabat() {
    setApplied(null)
    setBekraeftet(null)
    setDiscountCode('')
    setDiscountError(null)
    // Første træk uden rabatten.
    void hentPris(null)
  }

  async function handleBuy() {
    if (submitting || applying) return
    // En indtastet, men ikke anvendt kode tjekkes først: kunden skal se prisen (eller
    // fejlen) før betaling, og koden må aldrig falde stille bort.
    let rabat = applied
    let vistBetaling = foersteBetaling
    if (!rabat && discountCode.trim()) {
      rabat = await anvendRabat()
      if (!rabat) return
      // Første betaling med koden (samme svar som rabatten), ikke den fra før koden.
      vistBetaling = senesteFoersteTraek.betaling
    }
    setSubmitting(true)

    const result = await apiClient.post<CheckoutResponse>('/api/widget/memberships/checkout', {
      pricingPlanId: plan.id,
      ...(rabat ? { discountCode: rabat.code } : {}),
      // Kun et eksplicit valg når der var noget at vælge; ellers vælger serveren.
      ...(showPayPicker ? { paymentMethod: payMethod } : {}),
      // Den viste HELE første betaling (første træk/pris + gebyr). Checkout afviser ved et andet beløb.
      ...(vistBetaling !== null ? { expectedFirstChargeDkk: vistBetaling } : {}),
      // Vejen hjem. Den inline betaling går direkte til Stripe (uden om handoff-hoppet),
      // så uden returnUrl kunne kunden lande på VORES side efter en afbrudt betaling.
      // Serveren validerer adressen mod studiets godkendte widget-oprindelser.
      ...(returMaal() ? { returnUrl: returMaal() } : {}),
      legalConsent: checkoutConsent(legal, initData.locale),
    }, true)

    setSubmitting(false)

    if (result.error) {
      // AUTH_REQUIRED er sjælden (knappen er skjult når logget ud), men håndtér den.
      if (result.code === 'AUTH_REQUIRED') {
        authStore.clearTokens()
        return
      }
      // Et køb af samme plan er allerede i gang: vis serverens (lokaliserede) besked med
      // ventetiden. Handoffet ville sende kunden videre uden at sige hvad der skete.
      if (result.code === DUPLICATE_PAYMENT_CODE) {
        setNotice(result.error)
        return
      }
      // En ny dag er begyndt i studiets tidszone: serverens tekst (studiets sprog), og det
      // nye første træk hentes og vises. Intet er oprettet eller trukket; kunden prøver igen.
      if (result.code === FIRST_CHARGE_CHANGED) {
        setNotice(result.error ?? null)
        await hentPris(rabat?.code ?? null)
        // Serverens beløb vises som total og sendes ved næste forsøg (aldrig en løkke).
        if (typeof result.firstChargeDkk === 'number') setBekraeftet(result.firstChargeDkk)
        return
      }
      // Rabatkoden blev afvist ved selve købet (fx brugt op siden «Anvend», eller for
      // mange forsøg): vis det ved feltet i stedet for at sende kunden videre uden koden.
      if (rabat && (result.code === 'DISCOUNT_INVALID' || result.code === 'RATE_LIMITED')) {
        visRabatFejl(result.code, result.error)
        return
      }
      // Alt andet (komplekst scenarie, Stripe ej konfigureret, plan fuld):
      // widgeten kan ikke gennemføre købet inline → fald tilbage til handoffet.
      setNeedsHandoff(true)
      return
    }

    // Kernen oprettede en Stripe-session (eller et gratis medlemskab): send kunden videre.
    if (result.data?.redirectUrl) {
      navigerUd(result.data.redirectUrl)
      return
    }

    // Intet redirect og ingen fejl bør ikke ske — fald tilbage til handoffet for en sikkerheds skyld.
    setNeedsHandoff(true)
  }

  // Første betaling: første træk (ved «træk den 1.») eller prisen efter rabat, plus gebyret.
  // Et beløb checkout har bekræftet efter en afvisning vinder. null = endnu ikke hentet.
  const foersteDel = deferredDate ? 0 : firstCharge ? firstCharge.amount : applied ? applied.finalPrice : checkoutPrice
  const foersteBetaling = bekraeftet ?? (foersteDel === null ? null : Math.round((foersteDel + signupFee) * 100) / 100)

  const stop = (e: Event) => e.stopPropagation()

  return (
    <div class="hsw-pm-backdrop" onClick={onClose}>
      <style>{modalStyles}</style>
      <div class="hsw-pm-dialog" onClick={stop} role="dialog" aria-modal="true" aria-labelledby="hsw-pm-title">
        <button class="hsw-pm-close" onClick={onClose} aria-label={s.close}>×</button>

        <h3 id="hsw-pm-title" class="hsw-pm-title">{plan.name}</h3>
        <div class="hsw-pm-price">
          <span class="hsw-pm-price-label">{s.price}</span>
          <span class={applied ? 'hsw-pm-price-amount hsw-pm-price-old' : 'hsw-pm-price-amount'}>
            {formatMoney(checkoutPrice ?? plan.price, initData.locale, initData.currency, { keepOre: true })}
          </span>
        </div>
        {signupFee > 0 && (
          <div class="hsw-pm-discount-row" data-testid="hsw-pm-signup-fee">
            <span>{s.signupFeeLineLabel}</span>
            <span>{formatMoney(signupFee, initData.locale, initData.currency, { keepOre: true })}</span>
          </div>
        )}
        {deferredDate && (
          <div class="hsw-pm-discount-summary" data-testid="hsw-pm-deferred-first-charge">
            <p class="hsw-pm-discount-note">
              {s.deferredFirstChargeNote
                .replace('{date}', new Date(deferredDate).toLocaleDateString(initData.locale === 'da' ? 'da-DK' : initData.locale, { day: 'numeric', month: 'long', year: 'numeric' }))
                .replace('{price}', formatMoney(checkoutPrice ?? plan.price, initData.locale, initData.currency, { keepOre: true }))}
            </p>
          </div>
        )}
        {firstCharge && (
          <div class="hsw-pm-discount-summary" data-testid="hsw-pm-first-charge">
            <div class="hsw-pm-discount-row hsw-pm-discount-total">
              <span>{s.firstChargeLabel}</span>
              <span data-testid="hsw-pm-first-charge-amount">{formatMoney(firstCharge.amount, initData.locale, initData.currency, { keepOre: true })}</span>
            </div>
            <p class="hsw-pm-discount-note">
              {(firstCharge.folded ? s.firstChargeNoteFolded : s.firstChargeNote)
                .replace('{price}', formatMoney(firstCharge.recurringDkk, initData.locale, initData.currency, { keepOre: true }))}
            </p>
          </div>
        )}
        {applied && (
          <div class="hsw-pm-discount-summary" data-testid="hsw-pm-discount-summary">
            <div class="hsw-pm-discount-row">
              <span>{s.discountLine} ({applied.code})</span>
              <span>−{formatMoney(applied.amount, initData.locale, initData.currency, { keepOre: true })}</span>
            </div>
            <div class="hsw-pm-discount-row hsw-pm-discount-total">
              <span>{s.priceAfterDiscount}</span>
              <span>{formatMoney(applied.finalPrice, initData.locale, initData.currency, { keepOre: true })}</span>
            </div>
            {applied.appliesTo === 'first_payment' && <p class="hsw-pm-discount-note">{s.discountFirstPaymentOnly}</p>}
            {applied.appliesTo === 'every_payment' && <p class="hsw-pm-discount-note">{s.discountEveryPayment}</p>}
            {applied.freeMonthsBeforeBilling && (
              <p class="hsw-pm-discount-note">{s.discountFreeMonths.replace('{months}', String(applied.freeMonthsBeforeBilling))}</p>
            )}
          </div>
        )}

        {!user ? (
          // Inline login: LoginWidget skriver til authStore; subscriptionen ovenfor
          // fanger det og bytter denne branch for købs-trinnet.
          <div class="hsw-pm-state">
            <p>{s.buyLoginPrompt} <strong>{plan.name}</strong>.</p>
            <LoginWidget initData={initData} />
            <button class="hsw-pm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsHandoff ? (
          // Inline-køb kunne ikke gennemføres — send kunden til studiets egen prisside,
          // hvor hele billing-kernen (MobilePay/Flatpay/op-nedgradering m.m.) kører.
          <div class="hsw-pm-state">
            <p>{s.purchaseHandoffBody}</p>
            <a class="hsw-pm-primary" href={purchaseHref} target="_blank" rel="noopener" onClick={onClose}>
              {s.continueToPurchase}
            </a>
            <button class="hsw-pm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : (
          <div class="hsw-pm-state">
            {notice && <p class="hsw-pm-notice" role="alert">{notice}</p>}
            <div class="hsw-pm-discount">
              {/* Rabatkoder kan ikke bruges, når første betaling ligger senere (checkout afviser). */}
              {deferredDate ? null : applied ? (
                <button type="button" class="hsw-pm-discount-toggle" onClick={fjernRabat} disabled={submitting}>
                  {s.discountRemove} {applied.code}
                </button>
              ) : discountOpen ? (
                <div class="hsw-pm-discount-form">
                  <input
                    class="hsw-pm-discount-input"
                    type="text"
                    value={discountCode}
                    placeholder={s.discountCodePlaceholder}
                    aria-label={s.discountCodePlaceholder}
                    autocomplete="off"
                    autocapitalize="characters"
                    disabled={applying || submitting}
                    onInput={(e) => {
                      setDiscountCode((e.target as HTMLInputElement).value)
                      setDiscountError(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void anvendRabat() }
                    }}
                  />
                  <button
                    type="button"
                    class="hsw-pm-secondary"
                    onClick={() => void anvendRabat()}
                    disabled={applying || submitting || !discountCode.trim()}
                  >
                    {s.discountApply}
                  </button>
                </div>
              ) : (
                <button type="button" class="hsw-pm-discount-toggle" onClick={() => setDiscountOpen(true)}>
                  {s.discountCodeToggle}
                </button>
              )}
              {discountError && <p class="hsw-pm-discount-error" role="alert">{discountError}</p>}
            </div>
            {showPayPicker && (
              <PaymentMethodPicker
                initData={initData}
                value={payMethod}
                onChange={setPayMethod}
                disabled={submitting}
              />
            )}
            <CheckoutLegal
              s={s.checkoutLegal}
              initData={initData}
              lines={signupFee > 0 && foersteDel !== null && bekraeftet === null
                ? [
                    { label: `${plan.name}${firstCharge ? ` (${s.firstChargeLabel.toLowerCase()})` : ''}`, amount: formatMoney(foersteDel, initData.locale, initData.currency, { keepOre: true }) },
                    { label: s.signupFeeLineLabel, amount: formatMoney(signupFee, initData.locale, initData.currency, { keepOre: true }) },
                  ]
                : [{ label: plan.name }]}
              total={formatMoney(foersteBetaling ?? plan.price, initData.locale, initData.currency, { keepOre: true })}
              // Med oprettelsesgebyr eller et særskilt første træk er totalen første betaling, ikke prisen.
              totalLabel={firstPaymentTotalLabel(signupFee, !!firstCharge, s)}
              kind={plan.membershipCategory?.toLowerCase() === 'subscription' ? 'recurring' : 'one_time'}
              // En rabat kun på første betaling: totalen er første betaling, ikke prisen pr. periode.
              // «Træk den 1.»: totalen er første træk (resten af måneden), heller ikke pr. periode.
              // Senere startdato: totalen er 0 nu (første betaling står i noten), heller ikke pr. periode.
              billingValidityDays={deferredDate || firstCharge || signupFee > 0 || bekraeftet !== null || (applied && applied.appliesTo !== 'every_payment') ? undefined : (plan.validityDays ?? null)}
              showEarlyStart={earlyStartRelevantFor(plan.activatesFrom)}
              state={legal}
              onChange={setLegal}
              disabled={submitting}
            />
            <button class="hsw-pm-primary" onClick={handleBuy} disabled={submitting || applying || !legal.termsAccepted}>
              {submitting ? s.purchasing : s.checkoutLegal.payGeneric}
            </button>
            <button class="hsw-pm-secondary" onClick={onClose} disabled={submitting}>{s.cancel}</button>
          </div>
        )}
      </div>
    </div>
  )
}

const modalStyles = `
  .hsw-pm-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100000;
    padding: 1rem;
    animation: hsw-pm-fade .15s ease-out;
  }
  @keyframes hsw-pm-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .hsw-pm-dialog {
    position: relative;
    background: var(--yoga-card-bg, #ffffff);
    border-radius: var(--yoga-radius, 8px);
    padding: 2rem 1.75rem 1.75rem;
    width: 100%;
    max-width: 440px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    color: var(--yoga-text, #0a0a0a);
    box-sizing: border-box;
  }
  .hsw-pm-close {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    background: transparent;
    border: 0;
    font-size: 1.75rem;
    line-height: 1;
    color: var(--yoga-text-muted, #737373);
    cursor: pointer;
    padding: 0.25rem 0.6rem;
  }
  .hsw-pm-close:hover { color: var(--yoga-text, #0a0a0a); }
  .hsw-pm-title {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0 0 1rem;
    letter-spacing: -0.01em;
    line-height: 1.25;
  }
  .hsw-pm-price {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0.75rem 1rem;
    background: rgba(0,0,0,0.03);
    border-radius: var(--yoga-radius, 6px);
    margin-bottom: 1.25rem;
  }
  .hsw-pm-price-label {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-pm-price-amount {
    font-size: 1.1rem;
    font-weight: 700;
  }
  .hsw-pm-price-old {
    text-decoration: line-through;
    font-weight: 500;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-pm-discount-summary {
    margin: -0.75rem 0 1.25rem;
    padding: 0.6rem 1rem 0.75rem;
    background: rgba(0,0,0,0.03);
    border-radius: var(--yoga-radius, 6px);
    font-size: 0.9rem;
  }
  .hsw-pm-discount-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.15rem 0;
  }
  .hsw-pm-discount-total {
    font-weight: 700;
    font-size: 1.05rem;
  }
  .hsw-pm-discount-summary p.hsw-pm-discount-note,
  .hsw-pm-state p.hsw-pm-discount-note {
    margin: 0.35rem 0 0;
    font-size: 0.82rem;
  }
  .hsw-pm-discount-toggle {
    font: inherit;
    font-size: 0.875rem;
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    opacity: 0.7;
    text-decoration: underline;
    cursor: pointer;
    align-self: flex-start;
  }
  .hsw-pm-discount-toggle:hover { opacity: 1; }
  .hsw-pm-discount-form {
    display: flex;
    gap: 0.5rem;
  }
  .hsw-pm-discount-input {
    font: inherit;
    flex: 1;
    min-width: 0;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 6px);
    background: transparent;
    color: inherit;
    text-transform: uppercase;
  }
  .hsw-pm-state p.hsw-pm-discount-error {
    margin: 0.4rem 0 0;
    color: #b91c1c;
  }
  .hsw-pm-state {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .hsw-pm-state p.hsw-pm-notice {
    margin: 0;
    padding: 0.6rem 0.75rem;
    border-radius: 8px;
    background: #fffbeb;
    color: #92400e;
  }
  .hsw-pm-state p {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #525252);
    margin: 0;
    line-height: 1.5;
  }
  .hsw-pm-primary {
    display: inline-block;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.85rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background .2s, opacity .2s;
    text-decoration: none;
    text-align: center;
    font-family: inherit;
  }
  .hsw-pm-primary:hover:not(:disabled) {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-pm-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hsw-pm-secondary {
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.7rem 1.25rem;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background .2s;
    font-family: inherit;
  }
  .hsw-pm-secondary:hover:not(:disabled) {
    background: rgba(0,0,0,0.04);
  }
  .hsw-pm-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
